import { items } from "@/api/items/useItemQuery";
import { KPI } from "@/api/kpi/useKPIQuery";
import { getEndOfDate, getStartOfDate } from "@/utils/dateTimeUtil";
import { useQuery } from "@tanstack/react-query";

interface KPIDataProps {
  mode?: "equipment" | "item";
  value?: string | null;
  stateDate: [Date | null, Date | null];
  options: {
    fetchDefectRate?: boolean;
    fetchLeadTime?: boolean;
    fetchLeadTimeOrder?: boolean;
    fetchManhour?: boolean;
    fetchPowerUsage?: boolean;
    fetchUPH?: boolean;
    fetchRateOfOperation?: boolean;
    fetchAuditKPI?: boolean;
  };
}
export const useKPIData = ({ mode, value, stateDate, options = {} }: KPIDataProps) => {
  const {
    fetchDefectRate = false,
    fetchLeadTime = false,
    fetchLeadTimeOrder = false,
    fetchManhour = false,
    fetchPowerUsage = false,
    fetchUPH = false,
    fetchRateOfOperation = false,
    fetchAuditKPI = false,
  } = options;

  const { data } = useQuery({
    ...items.get({
      query: {
        $and: [{ code: value }],
      },
    }),
    enabled: !!value,
  });

  const itemData = data?.data?.rows && data?.data?.rows[0];

  const { data: equipments } = useQuery({
    ...items.get({
      query: {
        $and: [{ code: value }],
      },
    }),
    enabled: !!value,
  });

  const equipmentData = equipments?.data?.rows && equipments?.data?.rows[0];

  // 품질불량률
  const { data: defectData } = useQuery({
    ...KPI.defectRate({
      itemCode: value ?? "",
      scheduledAtFrom: getStartOfDate(stateDate[0]),
      scheduledAtTo: getEndOfDate(stateDate[1]),
    }),
    enabled: (mode === undefined || (mode !== undefined && !!value)) && fetchDefectRate,
  });

  // 제조 리드타임
  const { data: leadTimeData } = useQuery({
    ...KPI.leadTime({
      itemCode: value ?? "",
      scheduledAtFrom: getStartOfDate(stateDate[0]),
      scheduledAtTo: getEndOfDate(stateDate[1]),
    }),
    enabled: (mode === undefined || (mode !== undefined && !!value)) && fetchLeadTime,
  });

  // 수주/납품 리드타임
  const { data: leadTimeOrderData } = useQuery({
    ...KPI.leadTimeOrder({
      itemCode: value ?? "",
      scheduledAtFrom: stateDate[0]?.toISOString() ?? "",
      scheduledAtTo: stateDate[1]?.toISOString() ?? "",
    }),
    enabled: (mode === undefined || (mode !== undefined && !!value)) && fetchLeadTimeOrder,
  });

  // 작업공수
  const { data: manhourData } = useQuery({
    ...KPI.manhour({
      itemCode: value ?? "",
      scheduledAtFrom: getStartOfDate(stateDate[0]),
      scheduledAtTo: getEndOfDate(stateDate[1]),
    }),
    enabled: (mode === undefined || (mode !== undefined && !!value)) && fetchManhour,
  });
  // 전력사용량
  const { data: powerUsageData } = useQuery({
    ...KPI.powerUsage({
      itemCode: value ?? "",
      scheduledAtFrom: stateDate[0]?.toISOString() ?? "",
      scheduledAtTo: stateDate[1]?.toISOString() ?? "",
    }),
    enabled: (mode === undefined || (mode !== undefined && !!value)) && fetchPowerUsage,
  });
  // UPH
  const { data: UPHData } = useQuery({
    ...KPI.UPH({
      itemCode: value ?? "",
      scheduledAtFrom: getStartOfDate(stateDate[0]),
      scheduledAtTo: getEndOfDate(stateDate[1]),
    }),
    enabled: (mode === undefined || (mode !== undefined && !!value)) && fetchUPH,
  });

  // 설비가동률
  const { data: rateOfOperationData } = useQuery({
    ...KPI.rateOfOperation({
      equipmentCode: value ?? "",
      scheduledAtFrom: stateDate[0]?.toISOString() ?? "",
      scheduledAtTo: stateDate[1]?.toISOString() ?? "",
    }),
    enabled: (mode === undefined || (mode !== undefined && !!value)) && fetchRateOfOperation,
  });

  // 감리용 KPI 데이터
  const { data: auditKPIData } = useQuery({
    ...KPI.auditKPI({
      pageSize: 40,
      query: {
        $and: [
          {
            targetDate: {
              $between: [stateDate[0]?.toISOString(), stateDate[1]?.toISOString()],
            },
          },
          value && mode === "item" && { itemCode: { $eq: value } },
          value && mode === "equipment" && { equipmentCode: { $eq: value } },
        ].filter(Boolean),
      },
    }),
    enabled: (mode === undefined || (mode !== undefined && !!value)) && fetchAuditKPI,
  });

  return {
    itemData,
    equipmentData,
    defectData,
    leadTimeData,
    leadTimeOrderData,
    manhourData,
    powerUsageData,
    UPHData,
    rateOfOperationData,
    auditKPIData,
  };
};
